import * as z from "zod";
import { userFacingError } from "../authorization";
import { syncAllFacets, writeCommitFile } from "../commitFiles";
import { DEFAULT_WORKFLOW_RETENTION_SECONDS } from "../executionRetention";
import { zql } from "../generatedDrizzleSchema";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { sanitizeWorkflowName, uniqueWorkflowName } from "../workflowName";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export const importWorkflowSchema = z.object({
  id: clientGeneratableIdSchema,
  spaceId: z.string(),
  branchId: clientGeneratableIdSchema,
  commitId: clientGeneratableIdSchema,
  chatId: clientGeneratableIdSchema,
  systemEntryId: clientGeneratableIdSchema,
  name: z.string(),
  sourceFileName: z.string(),
  files: z.record(z.string(), z.string()),
  apps: z
    .array(
      z.object({
        stepDir: z.string(),
        name: z.string(),
        type: z.string(),
      })
    )
    .default([]),
});

export const importWorkflow = mutator({
  redactKeys: ["files"],
  schema: importWorkflowSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  description: "Creates a workflow by importing a ZIP archive.",
  interactionType: "create",
  fn: async ({
    tx,
    ctx,
    args: {
      id: workflowId,
      space,
      branchId,
      commitId,
      chatId,
      systemEntryId,
      name,
      sourceFileName,
      files,
    },
  }) => {
    const creator = creatorColumns(ctx);
    const actor = actorColumns(ctx);

    const paths = Object.keys(files);
    if (!paths.some((p) => p.endsWith("/config.toml"))) {
      throw userFacingError("ZIP does not contain any step config.toml files");
    }

    const now = Date.now();

    const siblings = await tx.run(
      zql.workflows.where("spaceId", space.id).where("deletedAt", "IS", null)
    );
    const uniqueName = uniqueWorkflowName(
      sanitizeWorkflowName(name),
      siblings.map((w) => w.name)
    );

    await tx.mutate.workflows.insert({
      id: workflowId,
      spaceId: space.id,
      name: uniqueName,
      needsName: false,
      createdAt: now,
      updatedAt: now,
      ...creator,
      autofixEnabled: true,
      autoImproveEnabled: false,
      workflowLinkHidden: false,
      retentionSeconds: DEFAULT_WORKFLOW_RETENTION_SECONDS,
      draftVolumeSeed: "ask",
      tenantId: ctx.tenantId,
    });

    await tx.mutate.spaces.update({
      id: space.id,
      updatedAt: now,
    });

    await tx.mutate.workflowBranches.insert({
      id: branchId,
      workflowId,
      userId: actor.userId,
      serviceAccountId: actor.serviceAccountId,
      name: "First draft",
      headCommitId: commitId,
      createdAt: now,
      lastChatEntryAt: now,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowCommits.insert({
      id: commitId,
      workflowId,
      branchId,
      parentCommitId: null,
      chatId,
      message: null,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.chats.insert({
      id: chatId,
      commitId,
      createdAt: now,
      lastChatEntryType: "SystemMessage",
      tenantId: ctx.tenantId,
    });

    await tx.mutate.chatEntries.insert({
      id: systemEntryId,
      chatId,
      entryType: "SystemMessage",
      createdAt: now,
      previousEntryId: null,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.systemMessages.insert({
      entryId: systemEntryId,
      text: `Imported from ${sourceFileName}`,
      tenantId: ctx.tenantId,
    });

    // Contributors are the human facepile; a pure service account records none.
    if (actor.userId) {
      await tx.mutate.branchContributors.insert({
        branchId,
        userId: actor.userId,
        tenantId: ctx.tenantId,
      });
    }

    const c = { tx, commitId, tenantId: ctx.tenantId };
    for (const [path, content] of Object.entries(files)) {
      await writeCommitFile(c, path, content);
    }
    await syncAllFacets(c);
  },
});
